/*!
 * DropStudios Drop 02 Ultimate v1.0.0 | Theme scripts
 * © 2026 DropStudios, a brand of Ma'at Studios LLC. All rights reserved.
 * https://www.maatstudios.co
 */

/* Dependency-free custom elements, prefixed ds-. Store data comes from window.DropStudios (layout/theme.liquid). */

const theme = window.DropStudios || {};

const fetchJSON = async (url, options = {}) => {
  const response = await fetch(url, { headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, ...options });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.description || data.message || theme.strings?.cartError);
  return data;
};

const htmlFrom = (markup, selector) => new DOMParser().parseFromString(markup, 'text/html').querySelector(selector);

/* Focus rings show only while the visitor navigates with the keyboard. */
document.addEventListener('keydown', (event) => {
  if (event.key === 'Tab') document.documentElement.classList.add('keyboard-nav');
});
document.addEventListener('pointerdown', () => document.documentElement.classList.remove('keyboard-nav'));

/** Keeps focus inside `container` while it is open; returns a release function. */
const trapFocus = (container) => {
  const selector = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';
  const onKeydown = (event) => {
    if (event.key !== 'Tab') return;
    const items = [...container.querySelectorAll(selector)].filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };
  container.addEventListener('keydown', onKeydown);
  return () => container.removeEventListener('keydown', onKeydown);
};

/** Base class for the menu drawer and the cart pop-over: open/close, Escape, overlay, focus trap, scroll lock. */
class DrawerElement extends HTMLElement {
  open(trigger) {
    this.trigger = trigger || document.activeElement;
    this.drawer.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    this.release = trapFocus(this.drawer);
    this.onKeyup = (event) => event.key === 'Escape' && this.close();
    document.addEventListener('keyup', this.onKeyup);
    requestAnimationFrame(() => (this.drawer.querySelector('[tabindex="-1"], button, a') || this.drawer).focus());
    this.trigger?.setAttribute?.('aria-expanded', 'true');
  }

  close() {
    this.drawer.hidden = true;
    document.documentElement.style.overflow = '';
    this.release?.();
    document.removeEventListener('keyup', this.onKeyup);
    this.trigger?.setAttribute?.('aria-expanded', 'false');
    this.trigger?.focus?.();
  }
}

class MenuDrawer extends DrawerElement {
  connectedCallback() {
    this.drawer = this.querySelector('.menu-drawer');
    this.toggle = this.querySelector('.header__menu-toggle');
    this.toggle?.addEventListener('click', () => (this.drawer.hidden ? this.open(this.toggle) : this.close()));
    this.drawer?.addEventListener('click', (event) => event.target.closest('[data-close]') && this.close());
  }
}
customElements.define('ds-menu-drawer', MenuDrawer);

class CartDrawer extends DrawerElement {
  connectedCallback() {
    this.drawer = this;
    this.addEventListener('click', (event) => event.target.closest('[data-close]') && this.close());
    document.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-cart-toggle]');
      if (!toggle || theme.cartType !== 'drawer') return;
      event.preventDefault();
      this.open(toggle);
    });
  }

  /* The card hangs below the header, which comes back if it was hidden. Without a pinned header it sits near the top of the screen. */
  open(trigger) {
    const header = document.querySelector('ds-sticky-header');
    let bottom = 0;
    if (header) {
      header.classList.remove('is-hidden');
      // Where the header rests, leaving out the slide it may still be finishing.
      bottom = header.getBoundingClientRect().bottom - new DOMMatrixReadOnly(getComputedStyle(header).transform).m42;
    }
    this.style.setProperty('--cart-top', `${Math.max(bottom, 0) + 8}px`);
    super.open(trigger);
  }

  renderContents(sections) {
    const markup = sections?.['cart-drawer'];
    if (!markup) return;
    const next = htmlFrom(markup, '#CartDrawerInner');
    if (next) this.querySelector('#CartDrawerInner').replaceWith(next);
  }
}
customElements.define('ds-cart-drawer', CartDrawer);

const updateCartCount = (count) => {
  document.querySelectorAll('[data-cart-count]').forEach((badge) => {
    badge.textContent = count;
    badge.classList.toggle('is-empty', count === 0);
  });
};

class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.button = this.querySelector('[type="submit"]');
    this.error = this.querySelector('[data-error]');
    this.form?.addEventListener('submit', (event) => this.onSubmit(event));
  }

  async onSubmit(event) {
    const drawer = document.querySelector('ds-cart-drawer');
    if (!drawer) return;
    event.preventDefault();
    if (this.button.getAttribute('aria-busy') === 'true') return;
    this.button.setAttribute('aria-busy', 'true');
    this.error.hidden = true;
    const body = Object.fromEntries(new FormData(this.form));
    body.sections = 'cart-drawer';
    body.sections_url = window.location.pathname;
    try {
      const response = await fetchJSON(`${theme.routes.cartAdd}.js`, { method: 'POST', body: JSON.stringify(body) });
      drawer.renderContents(response.sections);
      const cart = await fetchJSON(`${theme.routes.cart}.js`);
      updateCartCount(cart.item_count);
      drawer.open(this.button);
    } catch (error) {
      this.error.textContent = error.message;
      this.error.hidden = false;
    } finally {
      this.button.removeAttribute('aria-busy');
    }
  }
}
customElements.define('ds-product-form', ProductForm);

class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(this.querySelector('[data-variants]')?.textContent || '[]');
    this.addEventListener('change', () => this.onChange());
    this.markAvailability();
  }

  selectedOptions() {
    return [...this.querySelectorAll('fieldset')].map((fieldset) => {
      const checked = fieldset.querySelector('input:checked');
      const select = fieldset.querySelector('select');
      return checked ? checked.value : select?.value;
    });
  }

  markAvailability() {
    const selected = this.selectedOptions();
    this.querySelectorAll('fieldset').forEach((fieldset, index) => {
      fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
        const options = [...selected];
        options[index] = input.value;
        const match = this.variants.find((v) => v.options.every((value, i) => value === options[i]));
        input.classList.toggle('is-unavailable', !match || !match.available);
      });
    });
  }

  onChange() {
    const options = this.selectedOptions();
    const variant = this.variants.find((v) => v.options.every((value, i) => value === options[i]));
    this.querySelectorAll('fieldset').forEach((fieldset, i) => {
      const label = fieldset.querySelector('[data-selected-value]');
      if (label) label.textContent = options[i];
    });
    this.markAvailability();
    const form = document.getElementById(this.dataset.form);
    const button = form?.querySelector('[type="submit"]');
    const label = form?.querySelector('[data-add-label]');
    if (!variant) {
      if (button) button.disabled = true;
      if (label) label.textContent = theme.strings.unavailable;
      return;
    }
    const idInput = form?.querySelector('[data-variant-id]');
    if (idInput) idInput.value = variant.id;
    if (button) button.disabled = !variant.available;
    const prices = JSON.parse(form?.querySelector('[data-variant-prices]')?.textContent || 'null');
    const addLabel = prices ? `${theme.strings.addToCart} — ${prices[variant.id]}` : theme.strings.addToCart;
    if (label) label.textContent = variant.available ? addLabel : theme.strings.soldOut;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
    this.refreshSection(url);
    this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
    if (variant.featured_media) document.querySelector(`media-gallery [data-media-id="${variant.featured_media.id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
  }

  async refreshSection(url) {
    const section = this.dataset.section;
    const response = await fetch(`${url.pathname}?variant=${url.searchParams.get('variant')}&section_id=${section}`);
    const markup = await response.text();
    const price = htmlFrom(markup, `#Price-${section}`);
    if (price) document.getElementById(`Price-${section}`)?.replaceWith(price);
  }
}
customElements.define('ds-variant-picker', VariantPicker);

class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.addEventListener('click', (event) => {
      const button = event.target.closest('button');
      if (!button) return;
      const step = button.name === 'plus' ? 1 : -1;
      const min = Number(this.input.min || 0);
      this.input.value = Math.max(min, Number(this.input.value || 0) + step);
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }
}
customElements.define('ds-quantity-input', QuantityInput);

class CartItems extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', (event) => {
      const stepper = event.target.closest('ds-quantity-input[data-line]');
      if (!stepper) return;
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.update(Number(stepper.dataset.line), Number(event.target.value)), 300);
    });
    this.addEventListener('click', (event) => {
      const remove = event.target.closest('[data-remove-line]');
      if (remove) this.update(Number(remove.dataset.removeLine), 0);
    });
  }

  async update(line, quantity) {
    this.classList.add('is-loading');
    const inDrawer = !!this.closest('ds-cart-drawer');
    const section = inDrawer ? 'cart-drawer' : this.dataset.section;
    try {
      const cart = await fetchJSON(`${theme.routes.cartChange}.js`, {
        method: 'POST',
        body: JSON.stringify({ line, quantity, sections: section, sections_url: window.location.pathname }),
      });
      updateCartCount(cart.item_count);
      if (inDrawer) document.querySelector('ds-cart-drawer')?.renderContents(cart.sections);
      else if (section && cart.sections?.[section]) {
        // Cart page: swap in the re-rendered section instead of reloading.
        const next = htmlFrom(cart.sections[section], `#shopify-section-${section}`);
        const current = document.getElementById(`shopify-section-${section}`);
        if (next && current) current.replaceChildren(...next.childNodes);
        else window.location.reload();
      } else window.location.reload();
    } catch (error) {
      window.alert(error.message);
    } finally {
      this.classList.remove('is-loading');
    }
  }
}
customElements.define('ds-cart-items', CartItems);

class StickyHeader extends HTMLElement {
  connectedCallback() {
    const hideOnScroll = this.dataset.sticky === 'scroll-up';
    const transparent = this.hasAttribute('data-transparent');
    if (!hideOnScroll && !transparent) return;
    const staysTransparent = this.dataset.scrolledStyle === 'transparent';
    const first = document.querySelector('#MainContent > .shopify-section');
    let last = window.scrollY;
    const measure = () => document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
    measure();
    window.addEventListener('resize', measure);
    const update = () => {
      const y = window.scrollY;
      // A see-through bar switches to the page text color once it leaves the first section.
      const scrolled = staysTransparent && first ? first.getBoundingClientRect().bottom <= this.offsetHeight : y > 10;
      if (transparent) this.classList.toggle('is-scrolled', scrolled);
      if (hideOnScroll) this.classList.toggle('is-hidden', y > last && y > this.offsetHeight * 2);
      last = y;
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
  }
}
customElements.define('ds-sticky-header', StickyHeader);

/* Logo that alternates between the theme logos. An image only takes its turn once it has loaded, so the logo never blinks empty. */
class LogoRotator extends HTMLElement {
  connectedCallback() {
    this.images = [...this.children];
    if (this.images.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.index = 0;
    this.timer = setInterval(() => this.next(), Math.max(100, Number(this.dataset.interval) * 1000 || 200));
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  next() {
    const next = (this.index + 1) % this.images.length;
    if (document.hidden || this.images[next].complete === false) return;
    this.images[this.index].classList.remove('is-active');
    this.images[next].classList.add('is-active');
    this.index = next;
  }
}
customElements.define('ds-logo-rotator', LogoRotator);

/* Model carousel: garments slide over a model with the arrows, the dots, a swipe or the keyboard, and on their own after a pause. */
class ModelCarousel extends HTMLElement {
  connectedCallback() {
    this.slots = [...this.querySelectorAll('[data-slot]')];
    if (this.slots.length < 2) return;
    this.index = 0;
    this.delay = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : Number(this.dataset.autoplay) * 1000;
    this.stopped = false;
    this.visible = false;
    this.dots = [...this.querySelectorAll('[data-dot]')];
    this.name = this.querySelector('[data-name]:not([data-slot])');
    this.toggle = this.querySelector('[data-toggle]');
    // Nothing to pause when the visitor asked for less motion.
    if (this.toggle) this.toggle.hidden = !this.delay;

    this.querySelector('[data-previous]').addEventListener('click', () => this.step(-1));
    this.querySelector('[data-next]').addEventListener('click', () => this.step(1));
    this.toggle?.addEventListener('click', () => this.setStopped(!this.stopped));
    this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.show(index)));
    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.step(-1);
      if (event.key === 'ArrowRight') this.step(1);
    });
    // A garment at the side comes to the front when clicked; only the one in front follows its link.
    this.addEventListener('click', (event) => {
      const slot = event.target.closest('[data-slot]');
      if (!slot || (slot === this.slots[this.index] && !this.swiped)) return;
      event.preventDefault();
      if (!this.swiped) this.show(this.slots.indexOf(slot));
    });
    this.watchSwipe(this.querySelector('[data-stage]'));

    // Keyboard users keep the garment they are on.
    this.addEventListener('focusin', () => this.schedule());
    this.addEventListener('focusout', () => this.schedule());
    this.onVisibility = () => this.schedule();
    document.addEventListener('visibilitychange', this.onVisibility);
    this.observer = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
      this.schedule();
    }, { threshold: 0.4 });
    this.observer.observe(this);

    if (window.Shopify?.designMode) {
      this.addEventListener('shopify:block:select', (event) => {
        this.setStopped(true);
        this.show(this.slots.indexOf(event.target));
      });
    }
    this.show(0);
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    this.observer?.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  watchSwipe(stage) {
    let startX = null;
    stage.addEventListener('pointerdown', (event) => {
      startX = event.clientX;
      this.swiped = false;
    });
    stage.addEventListener('pointerup', (event) => {
      if (startX === null) return;
      const distance = event.clientX - startX;
      startX = null;
      if (Math.abs(distance) < 40) return;
      this.swiped = true;
      this.step(distance < 0 ? 1 : -1);
    });
    stage.addEventListener('pointercancel', () => (startX = null));
  }

  step(direction) {
    this.show((this.index + direction + this.slots.length) % this.slots.length);
  }

  show(index) {
    if (index < 0) return;
    this.index = index;
    this.style.setProperty('--index', index);
    this.slots.forEach((slot, i) => {
      const distance = Math.abs(i - index);
      slot.classList.toggle('is-active', distance === 0);
      slot.classList.toggle('is-near', distance === 1);
      slot.querySelector('a')?.setAttribute('tabindex', distance === 0 ? '0' : '-1');
    });
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === index);
      if (i === index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
    if (this.name) this.name.textContent = this.slots[index].dataset.name;
    this.schedule();
  }

  setStopped(stopped) {
    this.stopped = stopped;
    if (this.toggle) {
      this.toggle.classList.toggle('is-paused', stopped);
      this.toggle.setAttribute('aria-label', stopped ? this.toggle.dataset.labelPlay : this.toggle.dataset.labelPause);
    }
    this.schedule();
  }

  /* Restarts the wait before the next automatic change; any interaction calls it, so the visitor always gets the full time. */
  schedule() {
    clearTimeout(this.timer);
    if (!this.delay || this.stopped || !this.visible || document.hidden || this.querySelector(':focus-visible')) return;
    this.timer = setTimeout(() => this.step(1), this.delay);
  }
}
customElements.define('ds-model-carousel', ModelCarousel);


/* Infinite marquee: converts the speed setting (px/s on a 1920px canvas) into a loop duration. */
class Marquee extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.marquee__track');
    this.list = this.track.querySelector('.marquee__list');
    this.resize = () => this.fill();
    this.fill();
    window.addEventListener('resize', this.resize);
    this.list.querySelectorAll('img').forEach((img) => img.complete || img.addEventListener('load', this.resize, { once: true }));
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.resize);
  }

  /* Repeats the list so each half of the track is wider than the screen: the loop never shows a gap. */
  fill() {
    const width = this.list.offsetWidth;
    if (!width) return;
    const copies = Math.max(1, Math.ceil(this.offsetWidth / width)) * 2;
    const lists = this.track.querySelectorAll('.marquee__list');
    if (lists.length !== copies) {
      lists.forEach((list) => list !== this.list && list.remove());
      for (let i = 1; i < copies; i++) {
        const clone = this.list.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.inert = true;
        clone.querySelectorAll('a').forEach((a) => a.setAttribute('tabindex', '-1'));
        this.track.append(clone);
      }
    }
    this.updateDuration();
  }

  updateDuration() {
    const loop = this.track.scrollWidth / 2;
    const speed = Number(this.dataset.speed || 60) * Math.max(window.innerWidth / 1920, 0.35);
    if (loop > 0) this.style.setProperty('--marquee-duration', `${(loop / speed).toFixed(2)}s`);
  }
}
customElements.define('ds-marquee', Marquee);

/* Product gallery: swipe slider on phones; on larger screens one image at a time, changed by clicking its left or right half. */
class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('.product-gallery__slide')];
    this.dots = [...this.querySelectorAll('.product-gallery__dot')];
    this.list = this.querySelector('.product-gallery__slides');
    this.status = this.querySelector('[data-status]');
    this.desktop = window.matchMedia('(min-width: 750px)');
    this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
    if (this.slides.length < 2) return;

    this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1));
    this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1));
    this.dots.forEach((dot) => dot.addEventListener('click', () => this.go(Number(dot.dataset.index))));
    this.list.addEventListener('click', (event) => {
      if (!this.desktop.matches || event.target.closest('video, model-viewer, iframe')) return;
      this.go(this.index + (this.isLeftHalf(event) ? -1 : 1));
    });
    this.list.addEventListener('pointermove', (event) => this.classList.toggle('is-pointer-left', this.isLeftHalf(event)));
    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.go(this.index - 1);
      if (event.key === 'ArrowRight') this.go(this.index + 1);
    });
    this.list.addEventListener('scroll', () => this.onScroll(), { passive: true });
    this.desktop.addEventListener('change', () => this.go(this.index, false));
    this.closest('.shopify-section')?.addEventListener('variant:change', (event) => {
      const id = event.detail.variant?.featured_media?.id;
      const target = this.slides.findIndex((slide) => slide.dataset.mediaId === String(id));
      if (target !== -1) this.go(target);
    });
    this.go(this.index, false);
  }

  isLeftHalf(event) {
    const rect = this.list.getBoundingClientRect();
    return event.clientX - rect.left < rect.width / 2;
  }

  go(index, announce = true) {
    const count = this.slides.length;
    this.index = (index + count) % count;
    this.slides.forEach((slide, i) => {
      const active = i === this.index;
      slide.classList.toggle('is-active', active);
      if (!active) slide.querySelector('video')?.pause();
    });
    this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === this.index)));
    if (!this.desktop.matches) {
      this.scrolling = true;
      this.list.scrollTo({ left: this.slides[this.index].offsetLeft, behavior: announce ? 'smooth' : 'auto' });
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => (this.scrolling = false), 500);
    }
    if (announce && this.status) this.status.textContent = this.slides[this.index].getAttribute('aria-label');
  }

  onScroll() {
    if (this.desktop.matches || this.scrolling) return;
    const index = Math.round(this.list.scrollLeft / this.list.clientWidth);
    if (index === this.index) return;
    this.index = index;
    this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
    this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
  }
}
customElements.define('ds-product-gallery', ProductGallery);

/* Full-screen collection panels: videos only play while their panel is on screen. */
class CollectionStack extends HTMLElement {
  connectedCallback() {
    const videos = [...this.querySelectorAll('video')];
    if (!videos.length) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      videos.forEach((video) => video.pause());
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        if (entry.isIntersecting && video.offsetParent !== null) video.play().catch(() => {});
        else video.pause();
      });
    }, { threshold: 0.1 });
    videos.forEach((video) => {
      video.pause();
      observer.observe(video);
    });
  }
}
customElements.define('ds-collection-stack', CollectionStack);

/* Entry screen shown before the store. Remembers the visit; in the editor it shows while its section is selected. */
class EntryScreen extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute('data-design-mode')) {
      const id = this.dataset.sectionId;
      this.onSelect = (event) => event.detail.sectionId === id && this.classList.add('is-previewing');
      this.onDeselect = (event) => event.detail.sectionId === id && this.classList.remove('is-previewing');
      document.addEventListener('shopify:section:select', this.onSelect);
      document.addEventListener('shopify:section:deselect', this.onDeselect);
      return;
    }
    if (this.hidden) return;
    this.release = trapFocus(this);
    this.onKeyup = (event) => event.key === 'Escape' && this.close();
    document.addEventListener('keyup', this.onKeyup);
    requestAnimationFrame(() => this.querySelector('a, button')?.focus({ preventScroll: true }));
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-entry-close]')) return this.close();
      const link = event.target.closest('[data-entry-link]');
      if (!link) return;
      this.remember();
      const target = new URL(link.href, window.location.href);
      if (target.pathname === window.location.pathname && !target.search) {
        event.preventDefault();
        this.close();
      }
    });
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:section:select', this.onSelect);
    document.removeEventListener('shopify:section:deselect', this.onDeselect);
    document.removeEventListener('keyup', this.onKeyup);
  }

  remember() {
    const frequency = this.dataset.frequency;
    if (frequency === 'always') return;
    try {
      (frequency === 'session' ? sessionStorage : localStorage).setItem('ds-entry-seen', '1');
    } catch (error) {
      /* Storage can be unavailable (private mode); the screen simply shows again. */
    }
  }

  close() {
    this.remember();
    this.release?.();
    document.removeEventListener('keyup', this.onKeyup);
    document.documentElement.classList.remove('entry-screen-open');
    this.classList.add('is-leaving');
    this.querySelectorAll('video').forEach((video) => video.pause());
    setTimeout(() => (this.hidden = true), 600);
  }
}
customElements.define('ds-entry-screen', EntryScreen);

/* Password page: password form in a dialog (reopened after a wrong password). */
class PasswordPage extends HTMLElement {
  connectedCallback() {
    const dialog = this.querySelector('[data-password-dialog]');
    if (!dialog) return;
    this.querySelector('[data-password-open]')?.addEventListener('click', () => dialog.showModal());
    dialog.querySelector('[data-password-close]')?.addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => event.target === dialog && dialog.close());
    if (this.querySelector('[data-password-error]')) dialog.showModal();
  }
}
customElements.define('ds-password-page', PasswordPage);

/* Countdown to a launch date and time in the store's time zone; hides itself once it has passed. */
class LaunchCountdown extends HTMLElement {
  connectedCallback() {
    const offset = (this.dataset.offset || '+0000').replace(/(\d{2})(\d{2})$/, '$1:$2');
    this.target = Date.parse(`${this.dataset.date}T${this.dataset.time}:00${offset}`);
    if (Number.isNaN(this.target)) return;
    this.numbers = Object.fromEntries([...this.querySelectorAll('[data-unit]')].map((el) => [el.dataset.unit, el]));
    this.tick();
    this.timer = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  tick() {
    const left = Math.max(0, this.target - Date.now());
    this.hidden = left === 0;
    if (!left) return clearInterval(this.timer);
    const parts = { days: left / 864e5, hours: (left / 36e5) % 24, minutes: (left / 6e4) % 60, seconds: (left / 1e3) % 60 };
    for (const [unit, value] of Object.entries(parts)) {
      if (this.numbers[unit]) this.numbers[unit].textContent = String(Math.floor(value)).padStart(2, '0');
    }
  }
}
customElements.define('ds-launch-countdown', LaunchCountdown);

/* Side panel product list: a swipe carousel on phones, with progress bar and optional auto-slide. */
class ScrollingProducts extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('.scrolling-products__list');
    this.bar = this.querySelector('.scrolling-products__progress-bar');
    this.mobile = window.matchMedia('(max-width: 749px)');
    this.list.addEventListener('scroll', () => this.updateProgress(), { passive: true });
    window.addEventListener('resize', () => this.updateProgress());
    this.updateProgress();
    this.watchVideos();

    const delay = Number(this.dataset.autoplay) * 1000;
    if (!delay || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    ['pointerdown', 'touchstart', 'focusin', 'mouseenter'].forEach((type) => this.list.addEventListener(type, () => (this.pausedUntil = Date.now() + 6000), { passive: true }));
    new IntersectionObserver(([entry]) => (this.visible = entry.isIntersecting), { threshold: 0.5 }).observe(this.list);
    setInterval(() => this.advance(), delay);
  }

  /* The panel video only plays while it is on screen, and stays still for visitors who asked for less motion. */
  watchVideos() {
    const videos = [...this.querySelectorAll('video')];
    if (!videos.length) return;
    videos.forEach((video) => video.pause());
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        if (isIntersecting && target.offsetParent !== null) target.play().catch(() => {});
        else target.pause();
      });
    }, { threshold: 0.1 });
    videos.forEach((video) => observer.observe(video));
  }

  advance() {
    if (!this.mobile.matches || !this.visible || Date.now() < (this.pausedUntil || 0)) return;
    const item = this.list.querySelector('.scrolling-products__item');
    if (!item) return;
    const end = this.list.scrollLeft >= this.list.scrollWidth - this.list.clientWidth - 4;
    const step = item.offsetWidth + parseFloat(getComputedStyle(item.parentElement).columnGap || 0);
    this.list.scrollTo({ left: end ? 0 : this.list.scrollLeft + step, behavior: 'smooth' });
  }

  updateProgress() {
    if (!this.bar) return;
    const { scrollLeft, scrollWidth, clientWidth } = this.list;
    const thumb = Math.min(1, clientWidth / scrollWidth);
    const progress = scrollWidth > clientWidth ? scrollLeft / (scrollWidth - clientWidth) : 0;
    this.bar.style.setProperty('--thumb', `${thumb * 100}%`);
    this.bar.style.setProperty('--offset', `${progress * (1 / thumb - 1) * 100}%`);
  }
}
customElements.define('ds-scrolling-products', ScrollingProducts);

class DetailsModal extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelector('details');
    this.details.addEventListener('toggle', () => {
      if (this.details.open) this.querySelector('input[type="search"]')?.focus();
    });
    document.addEventListener('keyup', (event) => event.key === 'Escape' && this.details.removeAttribute('open'));
    document.addEventListener('click', (event) => !this.contains(event.target) && this.details.removeAttribute('open'));
  }
}
customElements.define('ds-details-modal', DetailsModal);

/* Pop-up opened by a button. Closes with its X, Escape or a click outside; the browser returns the focus to the button. */
class Popup extends HTMLElement {
  connectedCallback() {
    const dialog = this.querySelector('[data-popup]');
    this.querySelector('[data-popup-open]').addEventListener('click', () => dialog.showModal());
    dialog.querySelector('[data-popup-close]').addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => event.target === dialog && dialog.close());
  }
}
customElements.define('ds-popup', Popup);

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.closest('.search-modal')?.querySelector('[data-predictive-search]');
    this.input?.addEventListener('input', () => {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.search(this.input.value.trim()), 250);
    });
  }

  async search(terms) {
    if (!terms) {
      this.innerHTML = '';
      return;
    }
    const url = `${theme.routes.predictiveSearch}?q=${encodeURIComponent(terms)}&resources[type]=product&resources[limit]=4&section_id=${this.dataset.sectionId}`;
    const response = await fetch(url);
    if (!response.ok) return;
    const results = htmlFrom(await response.text(), '#PredictiveSearchResults');
    this.replaceChildren(...(results ? [results] : []));
  }
}
customElements.define('ds-predictive-search', PredictiveSearch);

class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    const observer = new IntersectionObserver(async (entries) => {
      if (!entries[0].isIntersecting) return;
      observer.disconnect();
      const response = await fetch(this.dataset.url);
      const next = htmlFrom(await response.text(), 'ds-product-recommendations');
      if (!next?.innerHTML.trim()) return;
      this.innerHTML = next.innerHTML;
      revealWithin(this);
    }, { rootMargin: '0px 0px 400px 0px' });
    observer.observe(this);
  }
}
customElements.define('ds-product-recommendations', ProductRecommendations);

/* Header dropdowns open on hover (pointer devices) and close on outside click. */
document.querySelectorAll('[data-hover-open]').forEach((details) => {
  const item = details.closest('li');
  if (window.matchMedia('(hover: hover)').matches) {
    item.addEventListener('mouseenter', () => details.setAttribute('open', ''));
    item.addEventListener('mouseleave', () => details.removeAttribute('open'));
  }
  document.addEventListener('click', (event) => !details.contains(event.target) && details.removeAttribute('open'));
});

/* Forms that submit on change (sorting, filters). */
document.querySelectorAll('form[data-auto-submit]').forEach((form) => form.addEventListener('change', () => form.submit()));

/* Customer login: toggle password recovery. */
document.querySelectorAll('[data-toggle-recover]').forEach((link) =>
  link.addEventListener('click', (event) => {
    event.preventDefault();
    document.getElementById('RecoverPassword')?.toggleAttribute('hidden');
    document.getElementById('CustomerLogin')?.toggleAttribute('hidden');
  }),
);
if (window.location.hash === '#recover') document.querySelector('[data-toggle-recover]')?.click();

/* Reveal on scroll. Also used for markup injected later (recommendations). */
function revealWithin(root) {
  const targets = root.querySelectorAll('[data-reveal]:not(.is-revealed)');
  if (!document.body.classList.contains('reveal-enabled') || !('IntersectionObserver' in window)) {
    targets.forEach((el) => el.classList.add('is-revealed'));
    return;
  }
  revealWithin.observer ||= new IntersectionObserver(
    (entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -10% 0px' },
  );
  targets.forEach((el) => revealWithin.observer.observe(el));
}
revealWithin(document);
